import { domainError, type DomainError } from '../shared/domain-error';
import { normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import { TAKEOFF_ERROR, TAKEOFF_ERROR_MESSAGE } from './errors';
import type { MeasureBy } from './geometry';
import { SHAFT_LABEL } from './shaft';

/**
 * A que o serviço se aplica — o **elemento** do desenho (Fase 24, D16).
 *
 * `MeasureBy` respondia duas perguntas com uma palavra só: *de que peça* e *que grandeza*.
 * Enquanto cada grandeza tinha uma peça só, ninguém notava — área de parede é da parede,
 * piso é do ambiente. O volume e a espala eram a exceção, e a exceção era um defeito:
 * `VOLUME` era "concreto de pilar **e** viga", e a CONCRETAGEM DE PILAR recebia o metro
 * cúbico das vigas junto. **Pilar e viga nunca se misturam** (RN-257).
 *
 * Com o elemento separado, a grandeza volta a ser só grandeza — volume, espala, área — e
 * quem diz de qual peça ela sai é este campo.
 *
 * `NENHUM` é o serviço que o desenho não mede: a quantidade é digitada (`UNIT`) ou veio
 * pronta do modelo (`MODEL`). Existe como valor, e não como ausência, porque ausência já
 * quer dizer outra coisa: **ainda ninguém escolheu** (ver `isPendingElement`).
 */
export type AppliesTo =
  | 'PAREDE'
  | 'AMBIENTE'
  | 'PILAR'
  | 'VIGA'
  | 'PORTA'
  | 'JANELA'
  | 'PASSAGEM'
  | 'GUARDA_CORPO'
  // O buraco na laje (D21) — não a `PASSAGEM` acima, que é o vão de parede sem folha.
  | 'SHAFT'
  // A estaca da fundação (Fase 27, ADR-175): peça própria, e não ambiente — nenhuma medida de
  // ambiente, parede, pilar ou viga a soma (RN-360).
  | 'ESTACA'
  // O bloco e a viga baldrame (Fase 27, etapa 2): peças da mesma família da estaca, e também
  // nenhuma delas entra nas medidas de ambiente, parede, pilar ou viga (RN-360).
  | 'BLOCO'
  | 'BALDRAME'
  // O radier e a laje (Fase 27, etapa 3): o contorno com espessura e regiões. Também peças
  // próprias — nenhuma medida de ambiente, parede, pilar, viga, estaca, bloco ou baldrame as
  // soma (RN-360).
  | 'RADIER'
  | 'LAJE'
  // O muro e o pilar de contenção (Fase 27, etapa 4): peças próprias, e nenhuma medida de
  // ambiente, parede, pilar, viga ou das peças anteriores as soma — nem elas somam as de lá
  // (RN-360, F2).
  | 'MURO_CONTENCAO'
  | 'PILAR_CONTENCAO'
  // A escada de concreto (Fase 27, etapa 5): peça própria, e nenhuma medida de ambiente — nem a do
  // ambiente ESCADA, que mede o acabamento — a soma, nem ela soma as de lá (RN-360, F17).
  | 'ESCADA'
  | 'NENHUM';

export const APPLIES_TO_LABEL: Readonly<Record<AppliesTo, string>> = {
  PAREDE: 'Parede',
  AMBIENTE: 'Ambiente',
  PILAR: 'Pilar',
  VIGA: 'Viga',
  PORTA: 'Porta',
  JANELA: 'Janela',
  PASSAGEM: 'Passagem',
  GUARDA_CORPO: 'Guarda-corpo',
  SHAFT: SHAFT_LABEL,
  ESTACA: 'Estaca',
  BLOCO: 'Bloco',
  BALDRAME: 'Viga baldrame',
  RADIER: 'Radier',
  LAJE: 'Laje',
  MURO_CONTENCAO: 'Muro de contenção',
  PILAR_CONTENCAO: 'Pilar de contenção',
  ESCADA: 'Escada',
  NENHUM: 'Nenhum (digitado)',
};

/**
 * Os pares válidos — **o único lugar** que diz quais existem (RN-256).
 *
 * A tela oferece daqui, o caso de uso recusa daqui e a conta mede daqui. Três listas
 * divergiriam no primeiro elemento novo, e o par inválido gravado é o pior dos três
 * defeitos: ele não mede nada e parece que mede.
 *
 * É a **matriz completa** (Fase 24, fatia 10, ADR-148): cada elemento com todas as medidas que
 * o desenho sabe tirar dele. A mesma grandeza aparece em vários — `COUNT` é de parede, de
 * pilar e de janela; `FACE` é de parede, de ambiente, de pilar e de viga —, e o que muda de um
 * elemento para o outro é a **conta**, que mora em `LEITURA_DO_PAR` (`geometry.ts`, lida por
 * `totalFor`), e o **texto**, que mora em `PAIR_TEXT`. Acrescentar uma linha ou uma medida é
 * acrescentar aos três, e o teste da matriz cobra que nenhum fique para trás.
 *
 * A ordem é a do select: a tela oferece na ordem em que está escrito aqui.
 */
export const MEASURES_BY_ELEMENT: Readonly<Record<AppliesTo, readonly MeasureBy[]>> = {
  PAREDE: ['WALL', 'FACE', 'LENGTH', 'VOLUME', 'COUNT'],
  AMBIENTE: ['FLOOR', 'CEILING', 'PERIMETER', 'SKIRTING', 'COUNT', 'FACE', 'VOLUME'],
  // O aço (Fase 27, F5) é o volume da peça vezes a taxa da disciplina: a ferragem de pilar e
  // a de viga medem cada uma a sua peça, como a concretagem (RN-257).
  PILAR: ['VOLUME', 'ESPALA', 'FACE', 'COUNT', 'LENGTH', 'ACO'],
  VIGA: ['VOLUME', 'ESPALA', 'FACE', 'COUNT', 'LENGTH', 'BOTTOM', 'ACO'],
  PORTA: ['AREA', 'COUNT', 'CONTOUR', 'WIDTH'],
  JANELA: ['AREA', 'COUNT', 'CONTOUR', 'WIDTH'],
  PASSAGEM: ['AREA', 'COUNT', 'CONTOUR', 'WIDTH'],
  // Comprado por metro, por metro quadrado ou por peça — do lado sem parede e de cima da
  // mureta (Fase 24, fatia 9 · RN-268).
  GUARDA_CORPO: ['LENGTH', 'AREA', 'COUNT'],
  // O shaft mede a peça dele — o buraco, o requadro e quantos são — e desconta do piso e
  // do teto do ambiente em que está (RN-270, RN-271).
  SHAFT: ['AREA', 'CONTOUR', 'COUNT'],
  // A estaca é do tipo dela e do que foi posicionado na planta (RN-362, RN-364). O aço vem do
  // tipo, e não da taxa da disciplina (RN-367); `MARKED` conta as marcadas **para o serviço**,
  // que é como o teste de resistência e o controle tecnológico medem (RN-365).
  ESTACA: ['ACO', 'VOLUME', 'LENGTH', 'COUNT', 'MARKED'],
  // O bloco e o baldrame medem o concreto e o que cerca o concreto: as faces laterais (a fôrma e
  // a impermeabilização) e o fundo (a compactação e o lastro). O aço é o volume vezes a taxa da
  // disciplina, como o do pilar e o da viga (RN-367) — só a estaca troca a taxa pelo tipo. O
  // baldrame, que é linha, mede também o comprimento livre (RN-368).
  BLOCO: ['ACO', 'VOLUME', 'FACE', 'BOTTOM', 'COUNT'],
  BALDRAME: ['ACO', 'VOLUME', 'FACE', 'BOTTOM', 'LENGTH', 'COUNT'],
  // O radier e a laje medem o contorno inteiro (a área: fôrma de fundo, escoramento, regularização)
  // e a borda (perímetro × espessura), e o concreto com a espessura de cada região (RN-369). O aço
  // é o volume vezes a taxa da disciplina (RN-367). A laje mede também o **fundo + borda** num
  // número só (`AREA_CONTOUR`): a fôrma dela é um serviço, e não dois (D7). O shaft tira a área e o
  // volume da laje (RN-370), e não os do radier.
  RADIER: ['ACO', 'VOLUME', 'AREA', 'CONTOUR', 'COUNT'],
  LAJE: ['ACO', 'VOLUME', 'AREA', 'CONTOUR', 'AREA_CONTOUR', 'COUNT'],
  // O muro mede a face em elevação (o m² do MURO DE CONTENÇÃO e da CORTINA ATIRANTADA, F7), o
  // volume e o comprimento; o pilar é moldado com fôrma (F6) e mede as faces, o volume e a altura.
  // O pilar encaixado tira do muro o que ocupa, e o muro tira do pilar as faces de dentro (RN-372).
  MURO_CONTENCAO: ['ACO', 'VOLUME', 'FACE', 'LENGTH', 'COUNT'],
  PILAR_CONTENCAO: ['ACO', 'VOLUME', 'FACE', 'LENGTH', 'COUNT'],
  // A escada mede a fôrma (fundo inclinado, espelhos, laterais e fundo do patamar), a projeção em
  // planta — o que o escoramento cobre —, o concreto e quantas escadas (F17).
  ESCADA: ['ACO', 'VOLUME', 'FACE', 'AREA', 'COUNT'],
  NENHUM: ['UNIT', 'MODEL'],
};

const ELEMENTOS = Object.keys(MEASURES_BY_ELEMENT) as readonly AppliesTo[];

/** Os elementos que a tela oferece: os que medem alguma coisa, na ordem da tabela. */
export const SELECTABLE_ELEMENTS: readonly AppliesTo[] = ELEMENTOS.filter(
  (elemento) => MEASURES_BY_ELEMENT[elemento].length > 0,
);

export function measuresFor(appliesTo: AppliesTo): readonly MeasureBy[] {
  return MEASURES_BY_ELEMENT[appliesTo];
}

export function isValidPair(appliesTo: AppliesTo, measureBy: MeasureBy): boolean {
  return MEASURES_BY_ELEMENT[appliesTo].includes(measureBy);
}

/** Os elementos que aceitam esta grandeza. */
export function elementsMeasuring(measureBy: MeasureBy): readonly AppliesTo[] {
  return ELEMENTOS.filter((elemento) => isValidPair(elemento, measureBy));
}

/**
 * O elemento que a grandeza já diz sozinha — área de parede é da parede.
 *
 * Só quando **um** elemento a aceita. Volume e espala servem ao pilar e à viga, e aí não
 * há implícito: escolher um dos dois por conta própria seria a mistura de volta, com o
 * sinal trocado. A resposta cresce sozinha com a tabela — no dia em que outro elemento
 * aceitar área, a área deixa de ter implícito, e ninguém precisa lembrar disso.
 *
 * A exceção é a grandeza que **já existia** quando passou a servir a mais de um elemento
 * (`ELEMENTO_DE_ORIGEM`): o serviço gravado antes dela não tem elemento escrito, e o dele é
 * o que sempre foi.
 */
export function impliedElement(measureBy: MeasureBy): AppliesTo | undefined {
  const aceitam = elementsMeasuring(measureBy);
  if (aceitam.length === 1) return aceitam[0];
  const deOrigem = ELEMENTO_DE_ORIGEM[measureBy];
  return deOrigem !== undefined && aceitam.includes(deOrigem) ? deOrigem : undefined;
}

/**
 * A grandeza que nasceu de um elemento e hoje serve a vários — e o elemento de antes.
 *
 * "Faces" era só da parede: todo serviço gravado com `FACE` e sem elemento é de parede (a
 * `0036` escreveu isso no banco, e o rascunho antigo do navegador não tem o campo). Quando o
 * ambiente, o pilar e a viga passaram a aceitá-la (fatia 10), o implícito por "uma peça só"
 * sumiria, e o reboco de sempre viraria pendente — sem medir — por uma mudança que não tocou
 * nele. Isto o mantém onde estava.
 *
 * Só para o que já existia. A grandeza **nova** em mais de um elemento — contagem, volume,
 * comprimento — segue pedindo a escolha: não há serviço antigo a proteger, e escolher por
 * ele é a mistura de volta (RN-257).
 */
const ELEMENTO_DE_ORIGEM: Readonly<Partial<Record<MeasureBy, AppliesTo>>> = {
  FACE: 'PAREDE',
  // "Fundo" era só da viga (o escoramento de viga). O bloco e o baldrame o passaram a medir na
  // Fase 27, e o escoramento de viga gravado sem elemento não pode virar pendente por isso.
  BOTTOM: 'VIGA',
};

/** O que o serviço precisa ter para dizer a que se aplica. */
export interface WithElement {
  readonly measureBy: MeasureBy;
  /** Ausente = o implícito da grandeza, quando há; sem implícito, **pendente**. */
  readonly appliesTo?: AppliesTo;
}

/**
 * A que o serviço se aplica, de fato — o escolhido, ou o que a grandeza implica.
 *
 * `undefined` é o serviço que **não mede nada**: volume ou espala sem peça escolhida, ou um
 * par que a tabela não conhece. Os dois ficam de fora da conta em vez de caírem num palpite
 * — medir a peça errada é pior do que não medir, porque o número parece certo.
 */
export function serviceElement(service: WithElement): AppliesTo | undefined {
  const elemento = service.appliesTo ?? impliedElement(service.measureBy);
  if (elemento === undefined) return undefined;
  return isValidPair(elemento, service.measureBy) ? elemento : undefined;
}

/**
 * O serviço espera alguém escolher o elemento (D17).
 *
 * É o que sobra da separação: o serviço antigo que media volume ou espala e cujo nome não
 * diz se é de pilar ou de viga. Ele não mede nada até alguém escolher, e a tela o marca.
 */
export function isPendingElement(service: WithElement): boolean {
  return serviceElement(service) === undefined;
}

// ── A margem da soleira e do peitoril ───────────────────────────────────────────

/**
 * Os vãos cuja largura somada leva margem (RN-308).
 *
 * Na obra a soleira é mais comprida que o vão da porta, e o peitoril, que o da janela: a
 * pedra passa alguns centímetros para cada lado, por baixo do batente ou do requadro. A
 * passagem entra junto porque tem soleira como a porta — só não tem folha.
 */
const VAOS_COM_MARGEM: ReadonlySet<AppliesTo> = new Set<AppliesTo>(['PORTA', 'JANELA', 'PASSAGEM']);

/** O serviço com a margem que ele leva — o que `totalFor` e o rótulo curto leem. */
export interface WithSideMargin extends WithElement {
  /** Metros para **cada** lado do vão. Ausente é zero (RN-308). */
  readonly sideMargin?: number;
  /**
   * Os kg de aço por m³ da disciplina do serviço, todas as bitolas somadas (Fase 27, RN-367).
   *
   * Não é do serviço: a taxa mora na disciplina (ADR-176), e quem monta a conta a pendura aqui
   * (`withSteelRate`) antes de perguntar ao desenho. Só a medida `ACO` a lê; ausente ou zero, o
   * aço não mede.
   */
  readonly steelRatePerM3?: number;
  /**
   * A chave do serviço no catálogo (`CatalogService.key`) — só a medida `MARKED` a lê.
   *
   * A estaca marcada é **por serviço** (D4, RN-365): o teste de resistência e o controle
   * tecnológico contam cada um as suas, e o desenho só sabe qual é qual pela chave. O serviço do
   * catálogo já a traz; quem monta um serviço à mão (um teste) a escreve, e sem ela o `MARKED`
   * não mede — contar as marcas de todos os serviços juntas seria número que parece certo.
   */
  readonly key?: string;
}

/**
 * O par aceita margem? Só a largura somada de porta, janela e passagem (RN-308).
 *
 * É o que a tela consulta para mostrar o campo — como a espessura só aparece sobre área —, e
 * o que a conta consulta para somá-la: margem gravada num par que não a aceita não muda
 * número nenhum, e o serviço que voltar à largura a reencontra.
 */
export function acceptsSideMargin(appliesTo: AppliesTo | undefined, measureBy: MeasureBy): boolean {
  return (
    appliesTo !== undefined &&
    measureBy === 'WIDTH' &&
    VAOS_COM_MARGEM.has(appliesTo) &&
    isValidPair(appliesTo, measureBy)
  );
}

/**
 * A margem que entra na conta: a do serviço, quando o par a aceita; zero no resto.
 *
 * Ausente é zero — a soleira rente ao vão é a de antes desta regra. Negativo também é zero:
 * o contrato e a tela já o recusam, e uma margem negativa que escapasse encurtaria a pedra
 * para dentro do vão, que não existe na obra.
 */
export function countedSideMargin(service: WithSideMargin): number {
  if (!acceptsSideMargin(serviceElement(service), service.measureBy)) return 0;
  const margem = service.sideMargin ?? 0;
  return Number.isFinite(margem) && margem > 0 ? margem : 0;
}

// ── O rótulo ────────────────────────────────────────────────────────────────────

/** A grandeza em uma palavra — o rótulo longo (`MEASURE_BY_LABEL`) explica, este identifica. */
export const MEASURE_SHORT_LABEL: Readonly<Record<MeasureBy, string>> = {
  WALL: 'parede',
  FACE: 'face',
  FLOOR: 'piso',
  CEILING: 'teto',
  PERIMETER: 'perímetro',
  SKIRTING: 'rodapé',
  ESPALA: 'espala',
  VOLUME: 'volume',
  UNIT: 'unidade',
  MODEL: 'modelo',
  COUNT: 'contagem',
  LENGTH: 'comprimento',
  // Do vão, do guarda-corpo (fatia 9) e do shaft (fatia 11): "do vão" sozinho diria a peça
  // errada aos dois.
  AREA: 'área',
  BOTTOM: 'fundo',
  CONTOUR: 'contorno',
  WIDTH: 'largura',
  ACO: 'aço',
  MARKED: 'marcadas',
  AREA_CONTOUR: 'fundo + borda',
};

/**
 * A unidade em que o desenho responde a cada grandeza. Vazia onde ele não responde.
 *
 * A unidade é da **grandeza**, e não do par: faces é m² na parede, no pilar e na viga. É o que
 * a tela escreve ao lado do número — sem ela, "3,48" não diz se são metros, metros quadrados
 * ou peças, e a contagem de janelas parecia área.
 */
export const MEASURE_UNIT: Readonly<Record<MeasureBy, string>> = {
  WALL: 'm²',
  FACE: 'm²',
  FLOOR: 'm²',
  CEILING: 'm²',
  PERIMETER: 'm',
  SKIRTING: 'm',
  ESPALA: 'm',
  VOLUME: 'm³',
  UNIT: '',
  MODEL: '',
  COUNT: 'un',
  LENGTH: 'm',
  AREA: 'm²',
  BOTTOM: 'm²',
  CONTOUR: 'm',
  WIDTH: 'm',
  ACO: 'kg',
  MARKED: 'un',
  AREA_CONTOUR: 'm²',
};

/**
 * Os elementos cujo contorno é uma **área** — a borda: perímetro × espessura (RN-369).
 *
 * `CONTOUR` é metro no vão (batente, requadro) e no shaft; no radier e na laje é a fôrma da borda,
 * que se compra em m². A unidade é da grandeza, e esta é a única exceção: o par diz a dele.
 */
const CONTORNO_EM_AREA: ReadonlySet<AppliesTo> = new Set<AppliesTo>(['RADIER', 'LAJE']);

/** O contorno do elemento é a borda em m², e não o requadro em metro? */
export function contourIsArea(appliesTo: AppliesTo | undefined): boolean {
  return appliesTo !== undefined && CONTORNO_EM_AREA.has(appliesTo);
}

/**
 * A unidade em que o desenho responde ao **par**: a da grandeza (`MEASURE_UNIT`), salvo o
 * contorno da borda do radier e da laje, que é m² (`contourIsArea`).
 */
export function measureUnit(appliesTo: AppliesTo | undefined, measureBy: MeasureBy): string {
  return measureBy === 'CONTOUR' && contourIsArea(appliesTo) ? 'm²' : MEASURE_UNIT[measureBy];
}

/** A grandeza é uma área — a única em que a espessura da camada vira volume. */
export function isAreaMeasure(measureBy: MeasureBy): boolean {
  return MEASURE_UNIT[measureBy] === 'm²';
}

/** O texto de um par: o que o select oferece, e como a lista curta o chama. */
export interface PairText {
  /** Quase a conta por extenso — "faces (4 × altura)". A unidade é `measureLabel` que acrescenta. */
  readonly label: string;
  /** Em poucas palavras, já com a peça — "faces de pilar". */
  readonly short: string;
}

/**
 * O texto de cada par da tabela — é o que distingue "faces" da parede, do pilar e da viga.
 *
 * A mesma grandeza lê-se de um jeito em cada elemento, porque a conta é outra: o rótulo diz a
 * conta (`4 × altura`, `2A + L`) em vez de mandar o usuário adivinhá-la. Par que a tabela
 * acrescentar e não puser aqui cai no texto da grandeza, sem quebrar — mas o teste cobra que
 * nenhum fique de fora.
 */
export const PAIR_TEXT: Readonly<
  Partial<Record<AppliesTo, Readonly<Partial<Record<MeasureBy, PairText>>>>>
> = {
  PAREDE: {
    WALL: { label: 'área de 1 lado, líquida dos vãos', short: 'parede' },
    FACE: { label: 'faces, 2 lados', short: 'face' },
    LENGTH: { label: 'comprimento', short: 'comprimento de parede' },
    VOLUME: { label: 'volume (área líquida × espessura)', short: 'volume de parede' },
    COUNT: { label: 'contagem de paredes', short: 'contagem de paredes' },
  },
  AMBIENTE: {
    FLOOR: { label: 'piso', short: 'piso' },
    CEILING: { label: 'teto', short: 'teto' },
    PERIMETER: { label: 'perímetro (cheio)', short: 'perímetro' },
    SKIRTING: { label: 'rodapé (perímetro menos as portas)', short: 'rodapé' },
    COUNT: { label: 'contagem de ambientes', short: 'contagem de ambientes' },
    FACE: {
      label: 'paredes do ambiente (perímetro × pé-direito, sem os vãos)',
      short: 'paredes do ambiente',
    },
    VOLUME: { label: 'volume (piso × pé-direito)', short: 'volume de ambiente' },
  },
  PILAR: {
    VOLUME: { label: 'volume de concreto', short: 'volume de pilar' },
    ESPALA: { label: 'espala (quina, metro linear)', short: 'espala de pilar' },
    FACE: { label: 'faces (4 × altura)', short: 'faces de pilar' },
    COUNT: { label: 'contagem de pilares', short: 'contagem de pilares' },
    LENGTH: { label: 'comprimento (altura somada)', short: 'comprimento de pilar' },
    ACO: { label: 'aço (volume × taxa de aço da disciplina)', short: 'aço de pilar' },
  },
  VIGA: {
    VOLUME: { label: 'volume de concreto', short: 'volume de viga' },
    ESPALA: { label: 'espala (quina, metro linear)', short: 'espala de viga' },
    FACE: { label: 'faces (2 laterais + fundo) × comprimento', short: 'faces de viga' },
    COUNT: { label: 'contagem de vigas', short: 'contagem de vigas' },
    LENGTH: { label: 'comprimento', short: 'comprimento de viga' },
    BOTTOM: { label: 'área de fundo (largura × comprimento)', short: 'fundo de viga' },
    ACO: { label: 'aço (volume × taxa de aço da disciplina)', short: 'aço de viga' },
  },
  PORTA: {
    AREA: { label: 'área do vão (L × A)', short: 'área de porta' },
    COUNT: { label: 'contagem de portas', short: 'contagem de portas' },
    CONTOUR: { label: 'batente / guarnição (2A + L)', short: 'batente de porta' },
    WIDTH: { label: 'largura somada (soleira)', short: 'largura de porta' },
  },
  JANELA: {
    AREA: { label: 'área do vão (L × A)', short: 'área de janela' },
    COUNT: { label: 'contagem de janelas', short: 'contagem de janelas' },
    CONTOUR: { label: 'contorno / requadro (2A + 2L)', short: 'contorno de janela' },
    WIDTH: { label: 'largura somada (peitoril)', short: 'largura de janela' },
  },
  PASSAGEM: {
    AREA: { label: 'área do vão (L × A)', short: 'área de passagem' },
    COUNT: { label: 'contagem de passagens', short: 'contagem de passagens' },
    CONTOUR: { label: 'requadro (2A + L)', short: 'requadro de passagem' },
    WIDTH: { label: 'largura somada', short: 'largura de passagem' },
  },
  // O do lado sem parede e o de cima da mureta (fatia 9, RN-268) — comprado de três jeitos.
  GUARDA_CORPO: {
    LENGTH: { label: 'comprimento (metro linear)', short: 'comprimento de guarda-corpo' },
    AREA: { label: 'área (altura × comprimento)', short: 'área de guarda-corpo' },
    COUNT: { label: 'contagem de guarda-corpos', short: 'contagem de guarda-corpos' },
  },
  // O buraco na laje (fatia 11, RN-270) — a área dele já saiu do piso e do teto do ambiente.
  SHAFT: {
    AREA: { label: 'área do buraco (L × C)', short: 'área de shaft' },
    CONTOUR: { label: 'requadro (2L + 2C)', short: 'requadro de shaft' },
    COUNT: { label: 'contagem de shafts', short: 'contagem de shafts' },
  },
  // A estaca posicionada na planta (RN-364): a da fila do "faltam N" ainda não conta.
  ESTACA: {
    ACO: { label: 'aço (o do tipo de cada estaca)', short: 'aço de estaca' },
    VOLUME: { label: 'volume (π·d²/4 × comprimento)', short: 'volume de estaca' },
    LENGTH: { label: 'comprimento (perfuração)', short: 'comprimento de estaca' },
    COUNT: { label: 'contagem de estacas', short: 'contagem de estacas' },
    MARKED: { label: 'estacas marcadas para este serviço', short: 'estacas marcadas' },
  },
  // O bloco mede o contorno e a altura que o desenho guarda (D3): a altura é a do concreto, do
  // fundo ao topo.
  BLOCO: {
    ACO: { label: 'aço (volume × taxa de aço da disciplina)', short: 'aço de bloco' },
    VOLUME: { label: 'volume (área em planta × altura)', short: 'volume de bloco' },
    FACE: { label: 'faces laterais (perímetro × altura)', short: 'faces de bloco' },
    BOTTOM: { label: 'área de fundo (a do contorno)', short: 'fundo de bloco' },
    COUNT: { label: 'contagem de blocos', short: 'contagem de blocos' },
  },
  // A viga baldrame mede o trecho **livre**: o eixo menos o que passa dentro dos blocos (RN-368).
  BALDRAME: {
    ACO: { label: 'aço (volume × taxa de aço da disciplina)', short: 'aço de baldrame' },
    VOLUME: {
      label: 'volume (largura × altura × comprimento livre)',
      short: 'volume de baldrame',
    },
    FACE: { label: 'faces laterais (2 × altura × comprimento livre)', short: 'faces de baldrame' },
    BOTTOM: {
      label: 'área de fundo (largura × comprimento livre)',
      short: 'fundo de baldrame',
    },
    LENGTH: {
      label: 'comprimento livre (o eixo menos o trecho dentro dos blocos)',
      short: 'comprimento de baldrame',
    },
    COUNT: { label: 'contagem de baldrames', short: 'contagem de baldrames' },
  },
  // O radier mede o contorno inteiro e o concreto de cada região (RN-369). O shaft não o fura.
  RADIER: {
    ACO: { label: 'aço (volume × taxa de aço da disciplina)', short: 'aço de radier' },
    VOLUME: {
      label: 'volume (área × espessura, com a de cada região)',
      short: 'volume de radier',
    },
    AREA: { label: 'área (o contorno inteiro)', short: 'área de radier' },
    CONTOUR: { label: 'borda (perímetro × espessura)', short: 'borda de radier' },
    COUNT: { label: 'contagem de radiers', short: 'contagem de radiers' },
  },
  // A laje desconta o shaft da área e do volume (RN-370).
  LAJE: {
    ACO: { label: 'aço (volume × taxa de aço da disciplina)', short: 'aço de laje' },
    VOLUME: {
      label: 'volume (área × espessura, com a de cada região, sem os shafts)',
      short: 'volume de laje',
    },
    AREA: { label: 'área (o contorno sem os shafts)', short: 'área de laje' },
    CONTOUR: { label: 'borda (perímetro × espessura)', short: 'borda de laje' },
    AREA_CONTOUR: {
      label: 'fundo + borda (área + perímetro × espessura)',
      short: 'fundo e borda de laje',
    },
    COUNT: { label: 'contagem de lajes', short: 'contagem de lajes' },
  },
  // O muro mede a face em elevação, sem os pilares encaixados (RN-372).
  MURO_CONTENCAO: {
    ACO: { label: 'aço (volume × taxa de aço da disciplina)', short: 'aço de muro de contenção' },
    VOLUME: {
      label: 'volume (face em elevação × espessura, sem os pilares)',
      short: 'volume de muro de contenção',
    },
    FACE: {
      label: 'face em elevação (comprimento × altura, sem os pilares encaixados)',
      short: 'face de muro de contenção',
    },
    LENGTH: { label: 'comprimento (o eixo do muro)', short: 'comprimento de muro de contenção' },
    COUNT: { label: 'contagem de muros de contenção', short: 'contagem de muros de contenção' },
  },
  // O pilar de contenção é moldado com fôrma, e perde as faces que ficam dentro do muro (RN-372).
  PILAR_CONTENCAO: {
    ACO: {
      label: 'aço (volume × taxa de aço da disciplina)',
      short: 'aço de pilar de contenção',
    },
    VOLUME: { label: 'volume (seção × altura)', short: 'volume de pilar de contenção' },
    FACE: {
      label: 'fôrma (perímetro × altura, sem as faces dentro do muro)',
      short: 'fôrma de pilar de contenção',
    },
    LENGTH: { label: 'altura (somada)', short: 'altura de pilar de contenção' },
    COUNT: { label: 'contagem de pilares de contenção', short: 'contagem de pilares de contenção' },
  },
  // A escada mede a fôrma inteira, a projeção que o escoramento cobre e o concreto (F17).
  ESCADA: {
    ACO: { label: 'aço (volume × taxa de aço da disciplina)', short: 'aço de escada' },
    VOLUME: {
      label: 'volume (laje inclinada + degraus + patamares)',
      short: 'volume de escada',
    },
    FACE: {
      label: 'fôrma (fundo inclinado, espelhos, laterais e fundo do patamar)',
      short: 'fôrma de escada',
    },
    AREA: { label: 'projeção em planta (lances e patamares)', short: 'projeção de escada' },
    COUNT: { label: 'contagem de escadas', short: 'contagem de escadas' },
  },
  NENHUM: {
    UNIT: { label: 'unidade digitada', short: 'unidade' },
    MODEL: { label: 'vem do modelo (IFC)', short: 'modelo' },
  },
};

/**
 * O par como o select escreve, com a unidade: "faces (4 × altura), m²".
 *
 * A unidade fecha o rótulo porque é ela que separa o que a conta dá: "contagem de janelas, un"
 * e "contorno / requadro (2A + 2L), m" não se confundem com uma área. Sem unidade (`UNIT`,
 * `MODEL`: o desenho não responde) o texto fica sozinho.
 */
export function measureLabel(appliesTo: AppliesTo, measureBy: MeasureBy): string {
  const texto = PAIR_TEXT[appliesTo]?.[measureBy]?.label ?? MEASURE_SHORT_LABEL[measureBy];
  const unidade = measureUnit(appliesTo, measureBy);
  return unidade === '' ? texto : `${texto}, ${unidade}`;
}

/**
 * A medida em poucas palavras, com a peça quando é ela que distingue.
 *
 * "parede" já diz de onde sai; "volume" não diz, e é aí que o elemento entra: "volume de
 * pilar", "faces de viga", "batente de porta". Sem peça escolhida, o rótulo diz isso em vez
 * de esconder — "volume sem elemento" numa exportação é o que faz alguém voltar ao cadastro.
 */
export function measureShortLabel(service: WithSideMargin): string {
  const curta = MEASURE_SHORT_LABEL[service.measureBy];
  const elemento = serviceElement(service);
  if (elemento === undefined) {
    return impliedElement(service.measureBy) !== undefined ? curta : `${curta} sem elemento`;
  }
  // Com margem, o número já não é a largura do vão: quem lê a exportação precisa saber que a
  // pedra passou dos dois lados (RN-310).
  if (countedSideMargin(service) > 0) {
    return `largura + margem de ${APPLIES_TO_LABEL[elemento].toLocaleLowerCase('pt-BR')}`;
  }
  const doPar = PAIR_TEXT[elemento]?.[service.measureBy]?.short;
  if (doPar !== undefined) return doPar;
  // Par sem texto próprio: a grandeza sozinha quando só uma peça a mede, com a peça senão.
  return elementsMeasuring(service.measureBy).length === 1
    ? curta
    : `${curta} de ${APPLIES_TO_LABEL[elemento].toLocaleLowerCase('pt-BR')}`;
}

// ── A decisão gravada ───────────────────────────────────────────────────────────

/**
 * O elemento que vai para o cadastro, conferido contra a tabela (RN-256).
 *
 * - **Pedido** vale se o par existe; par que não existe é recusado, e não corrigido —
 *   corrigir por conta própria gravaria um elemento que ninguém escolheu.
 * - **Não pedido** é "não disse", e não "nenhum": fica o **gravado**, se ele ainda casa com
 *   a grandeza; senão, o implícito dela; senão, pendente. É o que deixa a tela que ainda não
 *   conhece o campo gravar a medida sem apagar a peça que alguém escolheu.
 */
export function resolveAppliesTo(
  measureBy: MeasureBy,
  requested: AppliesTo | undefined,
  stored?: AppliesTo,
): Result<AppliesTo | undefined, DomainError> {
  if (requested !== undefined) {
    return isValidPair(requested, measureBy)
      ? ok(requested)
      : err(invalidAppliesToError(requested, measureBy));
  }
  if (stored !== undefined && isValidPair(stored, measureBy)) return ok(stored);
  return ok(impliedElement(measureBy));
}

export function invalidAppliesToError(appliesTo: AppliesTo, measureBy: MeasureBy): DomainError {
  return domainError(
    TAKEOFF_ERROR.INVALID_APPLIES_TO,
    TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.INVALID_APPLIES_TO]!,
    { appliesTo, measureBy },
  );
}

// ── O palpite ───────────────────────────────────────────────────────────────────

/**
 * As palavras que dizem a peça. **Palavra inteira**, e não pedaço dela.
 *
 * "VIGOTA" começa com "viga" e é da laje, não da viga — pegar por prefixo mandaria o
 * concreto da laje treliçada para a conta das vigas, calado. A migração `0036` usa a mesma
 * lista, com `\m` e `\M` do Postgres no lugar da separação em palavras daqui — e o
 * `aplica-a-migration.test.ts` cobra que as duas continuem iguais.
 */
export const ELEMENT_WORDS: readonly {
  readonly element: 'PILAR' | 'VIGA';
  readonly words: readonly string[];
}[] = [
  { element: 'PILAR', words: ['pilar', 'pilares', 'pilarete', 'pilaretes'] },
  { element: 'VIGA', words: ['viga', 'vigas'] },
];

/**
 * As palavras das peças que chegaram **depois** da `0036` — o shaft (Fase 24, D21).
 *
 * Ficam fora de `ELEMENT_WORDS` porque aquela lista é a da migração, e o teste que amarra
 * as duas cobraria do banco uma palavra que ele nunca palpitou: quando a `0036` rodou, o
 * shaft não existia e serviço nenhum media por ele. O palpite daqui é o mesmo, palavra
 * inteira (RN-273).
 */
export const LATER_ELEMENT_WORDS: readonly {
  readonly element: AppliesTo;
  readonly words: readonly string[];
}[] = [{ element: 'SHAFT', words: ['shaft', 'shafts'] }];

/**
 * As palavras do vão — porta, janela, passagem (fatia 10).
 *
 * Separadas de `ELEMENT_WORDS` de propósito: aquela lista é cobrada contra a `0036`, que só
 * migrou pilar e viga, e o vão é palpite de hoje para a frente — nenhum serviço antigo
 * precisa reconhecê-lo. **Palavra inteira**, como a de cima: "PORTAL" não é porta.
 */
const PALAVRAS_DE_VAO: readonly {
  readonly element: 'PORTA' | 'JANELA' | 'PASSAGEM';
  readonly words: readonly string[];
}[] = [
  { element: 'PORTA', words: ['porta', 'portas'] },
  { element: 'JANELA', words: ['janela', 'janelas'] },
  { element: 'PASSAGEM', words: ['passagem', 'passagens'] },
];

/**
 * Batente e guarnição são da porta — quando o nome não diz de qual vão.
 *
 * "BATENTE" sozinho é o da porta, e a obra o chama assim; "GUARNIÇÃO DE JANELA" diz a janela
 * e fica com ela. É o que permite ao serviço de nome curto, que é o comum, ter peça.
 */
const PALAVRAS_DE_BATENTE = ['batente', 'batentes', 'guarnicao', 'guarnicoes'];

/**
 * A soleira é da porta, e o peitoril, da janela — quando o nome não diz de qual vão (RN-309).
 *
 * Cada palavra diz a peça **e** a conta: a pedra corre ao longo do vão, então é a largura
 * somada dele (com a margem de cada lado). "SOLEIRA DE PASSAGEM" diz o vão e fica com ele.
 */
const PALAVRAS_DE_SOLEIRA = ['soleira', 'soleiras'];
const PALAVRAS_DE_PEITORIL = ['peitoril', 'peitoris'];

/** O que o nome chama de contorno do vão: o batente, o requadro, a guarnição. */
const PALAVRAS_DE_CONTORNO = [
  ...PALAVRAS_DE_BATENTE,
  'contorno',
  'contornos',
  'requadro',
  'requadros',
];

/** Fôrma, e a desforma que a desfaz — é o que faz o serviço medir as faces da peça. */
const PALAVRAS_DE_FORMA = ['forma', 'formas', 'desforma', 'desformas'];

/** Escoramento e cimbramento: o que sustenta o fundo da viga enquanto o concreto cura. */
const PALAVRAS_DE_ESCORAMENTO = ['escoramento', 'escoramentos', 'escora', 'escoras', 'cimbramento'];

/**
 * A grandeza que nasceu de um elemento só troca de peça com uma palavra de contexto.
 *
 * "FÔRMA DE PILAR" mede as faces do pilar; "PINTURA DO PILAR" continua sendo face de parede
 * — o pilar dentro da parede é rebocado e pintado como o resto dela (RN-186), e contar as
 * quatro faces seria cobrar de novo o que a parede já tem. Sem a palavra, o elemento de
 * origem fica, e o nome do pilar não o muda. O escoramento conta como a fôrma: é cobrado
 * pelas mesmas faces da peça (decidido em 01/10/2026).
 */
const CONTEXTO_DA_PECA: Readonly<Partial<Record<MeasureBy, readonly string[]>>> = {
  FACE: [...PALAVRAS_DE_FORMA, ...PALAVRAS_DE_ESCORAMENTO],
};

/** As palavras inteiras do texto, sem acento e em minúsculas. */
function palavrasDoTexto(texto: string): ReadonlySet<string> {
  return new Set(
    normalizeName(texto)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .split(/[^a-z0-9_]+/),
  );
}

/** As duas peças da estrutura — o resto das que o nome pode citar é vão (o shaft, na laje). */
const ESTRUTURAIS: ReadonlySet<AppliesTo> = new Set<AppliesTo>(['PILAR', 'VIGA']);

/** O texto tem alguma destas palavras? */
function temPalavra(texto: string, lista: readonly string[]): boolean {
  const palavras = palavrasDoTexto(texto);
  return lista.some((palavra) => palavras.has(palavra));
}

/**
 * A peça que o texto nomeia — nenhuma, uma, ou **várias**.
 *
 * Várias é o caso que decide sozinho o desenho da regra: "CONCRETAGEM DE PILARES E VIGAS"
 * é exatamente a mistura que esta fase desfaz, e escolher uma das peças mediria metade do
 * serviço com cara de inteiro.
 */
function pecasDoTexto(texto: string): ReadonlySet<AppliesTo> {
  const palavras = palavrasDoTexto(texto);
  const pecas = new Set<AppliesTo>(
    [...ELEMENT_WORDS, ...LATER_ELEMENT_WORDS, ...PALAVRAS_DE_VAO]
      .filter(({ words }) => words.some((palavra) => palavras.has(palavra)))
      .map(({ element }) => element),
  );
  // O batente que não diz de qual vão — o da parede ou o da laje — é o da porta.
  const temVao = [...pecas].some((peca) => !ESTRUTURAIS.has(peca));
  if (!temVao && PALAVRAS_DE_BATENTE.some((palavra) => palavras.has(palavra))) pecas.add('PORTA');
  // A soleira e o peitoril sem vão no nome, pela mesma regra (RN-309). Os dois juntos dão
  // duas peças, e o serviço fica pendente em vez de medir só uma delas.
  if (!temVao && PALAVRAS_DE_SOLEIRA.some((palavra) => palavras.has(palavra))) pecas.add('PORTA');
  if (!temVao && PALAVRAS_DE_PEITORIL.some((palavra) => palavras.has(palavra))) {
    pecas.add('JANELA');
  }
  return pecas;
}

/** Só as peças que a grandeza mede — o que o nome diz de outra não conta para ela. */
function pecasAceitas(texto: string, aceitam: readonly AppliesTo[]): ReadonlySet<AppliesTo> {
  return new Set([...pecasDoTexto(texto)].filter((peca) => aceitam.includes(peca)));
}

/**
 * O nome fala de guarda-corpo? Com hífen ou sem: as fichas escrevem "GUARDA-CORPO", e a
 * planilha, "GUARDA CORPO" — os dois são palpite do mesmo elemento (D25).
 */
export function mentionsRailing(texto: string): boolean {
  return normalizeName(texto)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/-/g, ' ')
    .includes('guarda corpo');
}

/**
 * Um palpite do elemento, para a tela nascer preenchida — a mesma regra de `guessMeasureBy`.
 *
 * A grandeza que tem elemento implícito não precisa de palpite. Volume e espala precisam, e
 * o nome resolve porque obra nomeia serviço pela peça: "CONCRETAGEM DE PILAR" (RN-258). O
 * nome **da disciplina** entra só quando o do serviço não diz nada — é o caso das etapas da
 * Fase 23, em que a disciplina é a peça (PILARES › CONCRETAGEM, ADR-141).
 *
 * A contagem, o comprimento e o contorno servem a vários elementos, e o nome dos vãos
 * resolve os seus: "PORTAS" é a contagem de portas, "BATENTE", o contorno da porta. O nome
 * que cita uma peça que a grandeza não mede não a atrapalha — só as que ela mede contam.
 *
 * Os dois nomes, ou nenhum, dão `undefined`: o serviço fica pendente e não mede, que é
 * errar para o lado seguro (D17).
 */
export function guessAppliesTo(
  measureBy: MeasureBy,
  serviceName: string,
  disciplineName?: string,
): AppliesTo | undefined {
  const implicito = impliedElement(measureBy);
  // A grandeza de origem (faces) só troca de peça com a palavra de contexto: "FÔRMA DE PILAR"
  // é do pilar, "PINTURA DO PILAR" continua sendo de parede.
  const contexto = CONTEXTO_DA_PECA[measureBy];
  const trocaDePeca = contexto !== undefined && temPalavra(serviceName, contexto);
  if (implicito !== undefined && !trocaDePeca) return implicito;

  const aceitam = elementsMeasuring(measureBy);
  // O guarda-corpo pelo nome do serviço, antes das outras peças: "GUARDA-CORPO" não cita
  // palavra de peça nenhuma (são duas, com hífen), e é ele que decide quando a grandeza serve
  // a mais de uma peça (D25).
  if (aceitam.includes('GUARDA_CORPO') && mentionsRailing(serviceName)) return 'GUARDA_CORPO';
  const doServico = pecasAceitas(serviceName, aceitam);
  const pecas =
    doServico.size === 0 && disciplineName !== undefined
      ? pecasAceitas(disciplineName, aceitam)
      : doServico;
  // Sem peça no nome, o de origem fica (só existe quando o contexto pediu a troca).
  if (pecas.size === 0) return implicito;
  if (pecas.size !== 1) return undefined;

  const [peca] = [...pecas] as [AppliesTo];
  return peca;
}

/**
 * O palpite da **grandeza** quando o nome diz a peça e a conta — fôrma, escoramento, batente.
 *
 * É o que o `guessMeasureBy` consulta antes da lista de palavras dele, porque estes nomes
 * têm as duas palavras: "FÔRMA DE PILAR" tem "forma" e "pilar", e nenhum termo sozinho o
 * resolveria. Vale só com **uma** peça do tipo no nome — "FÔRMA DE PILARES E VIGAS" é a
 * mistura de volta (RN-257), e fica para quem escolher.
 *
 * - fôrma **e escoramento** de pilar ou de viga → `FACE`: as quatro faces do pilar, as três
 *   da viga, em m² (decidido com o usuário em 01/10/2026 — o escoramento é cobrado pela
 *   mesma área da fôrma, e não pelo fundo; o fundo continua escolhível à mão);
 * - batente, guarnição ou requadro de porta, janela, passagem ou shaft → `CONTOUR`;
 * - soleira ou peitoril → `WIDTH`, a largura somada do vão com a margem de cada lado (decidido
 *   com o usuário em 01/10/2026, RN-309). Antes era o perímetro do ambiente, que mede a sala
 *   inteira e não a pedra. Soleira **e** peitoril no mesmo nome continuam largura, e a peça
 *   fica pendente (`guessAppliesTo`).
 *
 * É palpite, e a tela o diz: o que alguém escolher por cima manda (`measureOrigin`).
 */
export function guessMeasureOfPiece(name: string): MeasureBy | undefined {
  const pecas = [...pecasDoTexto(name)];
  const estruturais = pecas.filter((peca) => ESTRUTURAIS.has(peca));
  const vaos = pecas.filter((peca) => !ESTRUTURAIS.has(peca));

  if (estruturais.length === 1 && vaos.length === 0) {
    if (temPalavra(name, PALAVRAS_DE_FORMA) || temPalavra(name, PALAVRAS_DE_ESCORAMENTO)) {
      return 'FACE';
    }
  }
  if (vaos.length === 1 && estruturais.length === 0 && temPalavra(name, PALAVRAS_DE_CONTORNO)) {
    return 'CONTOUR';
  }
  if (
    estruturais.length === 0 &&
    temPalavra(name, [...PALAVRAS_DE_SOLEIRA, ...PALAVRAS_DE_PEITORIL])
  ) {
    return 'WIDTH';
  }
  return undefined;
}

/**
 * O palpite da grandeza do serviço que só nomeia um vão: "PORTAS" é a contagem de portas.
 *
 * Fica **depois** da lista de palavras do `guessMeasureBy`, e de propósito: "PINTURA DE
 * PORTA" já era face, e a contagem só ocupa o que o nome não resolvia por outro lado.
 * Dois vãos no nome ("JANELAS E PORTAS DE VIDRO") é serviço que conta os dois, e nenhuma
 * contagem sozinha seria a dele.
 */
export function guessMeasureOfOpening(name: string): MeasureBy | undefined {
  const pecas = [...pecasDoTexto(name)];
  const soVao = pecas.length === 1 && PALAVRAS_DE_VAO.some(({ element }) => element === pecas[0]);
  return soVao ? 'COUNT' : undefined;
}
